/* EDF Form sub-panel — one Export Declaration Form per invoice; type freight + pick bank, then print. */
import { DocAcksBanner } from "@/components/DocAcksBanner";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BankSelect } from "@/components/BankSelect";
import { EdfNumbersCard } from "@/components/EdfNumbersCard";
import { Download, Eye, FileCheck2, Loader2, Search, Trash2, Upload } from "lucide-react";

const H = () => ({ headers: getAuthHeader() });
const STATUS = {
  needs_bank: ["Pick bank", "bg-amber-100 text-amber-800"],
  needs_freight: ["Enter freight", "bg-sky-100 text-sky-800"],
  needs_edf_no: ["No EDF number", "bg-rose-100 text-rose-800"],
  ready: ["Ready", "bg-emerald-100 text-emerald-800"],
};

export const openPdf = async (id, inline, base = "edf-doc") => {
  let r;
  try {
    r = await axios.get(`${API}/${base}/history/${id}/download`, { ...H(), responseType: "blob" });
  } catch (e) { toast.error("PDF not ready yet"); return; }
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  if (inline) { window.open(url, "_blank"); return; }
  const a = document.createElement("a");
  a.href = url; a.download = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "EDF.pdf"; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const EdfRow = ({ r, banks, isAdmin, onSaved, onDelete, onBankAdded }) => {
  const [fr, setFr] = useState(r.freight_set ? String(r.freight_usd) : "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFr(r.freight_set ? String(r.freight_usd) : ""); }, [r.freight_set, r.freight_usd]);
  const save = async (patch) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/edf-doc/history/${r.id}`, patch, H());
      onSaved(res.data);
      if (res.data.status === "ready") toast.success(`EDF ${r.invoice_no} ready`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      if ("freight_usd" in patch) setFr(r.freight_set ? String(r.freight_usd) : "");
    }
    setBusy(false);
  };
  const [lbl, cls] = STATUS[r.status] || STATUS.needs_bank;
  const fmt = (v) => Number(v || 0).toFixed(2);
  return (
    <tr className="border-b last:border-0 hover:bg-slate-50" data-testid={`edf-row-${r.invoice_no}`}>
      <td data-label="Invoice" className="py-2 font-mono font-semibold text-indigo-700">{r.invoice_no}
        <div className="font-mono text-[11px] font-bold text-slate-800" data-testid={`edf-no-${r.invoice_no}`}>{r.edf_no ? `EDF ${r.edf_no}` : <span className="font-sans font-normal text-rose-600">no EDF no.</span>}</div></td>
      <td data-label="Date" className="text-slate-600">{r.invoice_date}</td>
      <td data-label="Buyer" className="max-w-[160px] truncate" title={r.buyer}>{r.buyer}</td>
      <td data-label="Company" className="text-xs text-slate-600">{r.company_name}</td>
      <td data-label="FOB ($)" className="text-right font-semibold" data-testid={`edf-fob-${r.invoice_no}`}>${fmt(r.fob_usd)}</td>
      <td data-label="Freight ($)" className="text-center">
        <Input value={fr} onChange={(e) => setFr(e.target.value)} placeholder="$"
          onBlur={() => fr.trim() !== "" && Number(fr) !== (r.freight_set ? r.freight_usd : NaN) && save({ freight_usd: fr })}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className="mx-auto h-7 w-20 text-right" data-testid={`edf-freight-${r.invoice_no}`} />
      </td>
      <td data-label="Rate" className="text-right text-xs" data-testid={`edf-rate-${r.invoice_no}`}>{fmt(r.rate)}</td>
      <td data-label="INR" className="pr-3 text-right font-semibold" data-testid={`edf-inr-${r.invoice_no}`}>₹{fmt((r.fob_usd + (r.freight_usd || 0)) * r.rate)}</td>
      <td data-label="Bank (AD code)">
        <BankSelect companyId={r.company_id} companyName={r.company_name} banks={banks} value={r.bank_id} disabled={busy}
          onPick={(id) => save({ bank_id: id })} onAdded={(b) => onBankAdded(r.company_id, b)} testid={`edf-bank-${r.invoice_no}`} />
      </td>
      <td data-label="Status"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={`edf-status-${r.invoice_no}`}>{busy ? "Saving…" : lbl}</span></td>
      <td data-label="Actions" className="text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={r.status !== "ready"} onClick={() => openPdf(r.id, true)} data-testid={`edf-view-${r.invoice_no}`}><Eye className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={r.status !== "ready"} onClick={() => openPdf(r.id, false)} data-testid={`edf-dl-${r.invoice_no}`}><Download className="h-4 w-4" /></Button>
        {isAdmin && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => onDelete(r)} data-testid={`edf-del-${r.invoice_no}`}><Trash2 className="h-4 w-4" /></Button>}
      </td>
    </tr>
  );
};

export default function EdfDocPanel({ user }) {
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [banks, setBanks] = useState({});
  const [q, setQ] = useState("");
  const [up, setUp] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/edf-doc/history`, { ...H(), params: { q } }).catch(() => ({ data: { rows: [] } }));
    setRows(r.data.rows);
    const ids = [...new Set(r.data.rows.map(x => x.company_id).filter(Boolean))];
    const got = await Promise.all(ids.map(id => axios.get(`${API}/edf-doc/banks/${id}`, H()).then(b => [id, b.data.banks]).catch(() => [id, []])));
    setBanks(Object.fromEntries(got));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const upload = async (f) => {
    if (!f) return;
    setUp(true);
    const fd = new FormData(); fd.append("packing_slip", f);
    try {
      const r = await axios.post(`${API}/edf-doc/generate`, fd, H());
      toast.success(`EDF ${r.data.invoice_no} drafted — FOB $${r.data.fob_usd}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read this packing slip"); }
    setUp(false); if (fileRef.current) fileRef.current.value = "";
  };
  const del = async (r) => {
    if (!window.confirm(`Delete EDF ${r.invoice_no}?`)) return;
    await axios.delete(`${API}/edf-doc/history/${r.id}`, H()).catch(() => toast.error("Delete failed")); load();
  };
  const saved = (e) => setRows(rs => rs.map(x => (x.id === e.id ? { ...x, ...e } : x)));
  const bankAdded = (cid, b) => setBanks(m => ({ ...m, [cid]: [...(m[cid] || []), b] }));
  return (
    <div className="space-y-5" data-testid="edf-doc-panel">
      <DocAcksBanner />
      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800"><FileCheck2 className="h-5 w-5 text-indigo-600" /> EDF Form</h2>
        <p className="mt-1 text-sm text-slate-500">Drafted automatically for each invoice when its packing slip is uploaded in Order Register. FOB = packing slip total ($); INR uses the Order Register $ rate of the invoice date. Type freight and pick the bank to make it ready.</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="edf-upload-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={up} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edf-upload-btn">
            {up ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload packing slip
          </Button>
        </div>
      </div>
      <EdfNumbersCard onChanged={load} />
      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h3 className="font-semibold text-slate-700">EDF forms</h3>
          <div className="relative ml-auto w-72">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice, buyer, company" className="pl-8" data-testid="edf-search" />
          </div>
        </div>
        {rows === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /> : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="edf-empty">No EDF forms yet.</p>) : (
          <div className="overflow-x-auto">
            <table className="doc-cards w-full text-sm" data-testid="edf-table">
              <thead><tr className="border-b text-left text-xs text-slate-500">
                <th className="py-2">Invoice</th><th>Date</th><th>Buyer</th><th>Company</th><th className="text-right">FOB ($)</th>
                <th className="text-center">Freight ($)</th><th className="text-right">Rate</th><th className="pr-3 text-right">INR</th><th>Bank (AD code)</th><th>Status</th><th className="text-right">Actions</th>
              </tr></thead>
              <tbody>{rows.map(r => <EdfRow key={r.id} r={r} banks={banks[r.company_id] || []} isAdmin={isAdmin} onSaved={saved} onDelete={del} onBankAdded={bankAdded} />)}</tbody>
            </table>
          </div>)}
      </div>
    </div>
  );
}
